<template>

    <div class="flex items-center justify-end gap-x-4">
        <div class="w-full mr-auto">
            <text-field
                v-model="tagSearchInput"
                v-on:keyup.enter="createNewTag"
                @update:modelValue="filterTagChange"
                placeholder="Filter tags"></text-field>
        </div>
        <div class="shrink-0" v-if="newTagAvailable">
            <btn type="secondary" @click="createNewTag" :loading="loading.addingPartnerTag">Create new tag</btn>
        </div>
        <div class="shrink-0">
            <modal size="small" title="Manage tags" cancel_btn_title="Close">
                <template #toggle>
                    <btn type="secondary">Edit tags</btn>
                </template>
                <template #body>
                    <div class="space-y-6">
                        <program-partner-tags
                            :program="parseInt(program)"
                            :programPartnerTags="availablePartnerTags"
                            @update:program-partner-tags="programPartnerTagsUpdate"
                        ></program-partner-tags>
                    </div>
                </template>
            </modal>
        </div>
    </div>

    <div class="flex gap-4 mt-5">
        <!-- Show tags if any are visible -->
        <div
            v-for="availableTag in availablePartnerTags"
            v-show="availableTag.show ?? true"
            :key="availableTag.key"
        >
            <btn
                class="flex-shrink-0 rounded-full"
                @click="toggleTag(availableTag.key)"
                :type="partnerTags.includes(availableTag.key) ? 'primary' : 'white'">
                {{ availableTag.value }}
            </btn>
        </div>

        <!-- Placeholder if no tags are shown -->
        <div
            v-if="!availablePartnerTags.some(tag => tag.show ?? true)"
            class="border rounded w-full p-6 text-sm text-gray-500 text-center"
        >
            No tags available.
        </div>
    </div>
</template>

<script setup>

import {ref, onMounted} from 'vue';
import TextField from "../../../../elements/forms/TextField.vue";
import ProgramPartnerTags from "./ProgramPartnerTags.vue";
import Btn from "../../../../elements/Btn.vue";

const props = defineProps({
    partner: {
        type: Number,
        required: true
    },
    program: {
        type: Number,
        required: true
    },
    partnerTags: {
        type: Array,
        default: []
    },
    availablePartnerTags: {
        type: Array,
        default: []
    },
});

let searchTimeout = null;
const tagSearchInput = ref('');
const newTagAvailable = ref(false);
const partnerTags = ref(props.partnerTags);
const availablePartnerTags = ref(props.availablePartnerTags);

const loading = ref({
    addingPartnerTag: false,
});

const filterTagChange = (input) => {

    isNewTagAvailable(input);

    clearTimeout(searchTimeout);
    searchTimeout = setTimeout(() => {
        availablePartnerTags.value = filterTags(input);
    }, 250);
}

const isNewTagAvailable = (input) => {
    input = input.trim().toLowerCase();

    if (input === '') {
        newTagAvailable.value = false;
        return;
    }

    for (const [i, availableTag] of Object.entries(availablePartnerTags.value)) {
        if (availableTag.value.toLowerCase() === input) {
            newTagAvailable.value = false;
            return;
        }
    }

    newTagAvailable.value = true;
}

const toggleTag = (tag) => {

    let checked = !(partnerTags.value.includes(tag));

    if (checked && !partnerTags.value.includes(tag)) {
        partnerTags.value.push(tag);
    }

    if (!checked && partnerTags.value.includes(tag)) {
        partnerTags.value.splice(partnerTags.value.indexOf(tag), 1);
    }

    editTags();
}

const filterTags = (input, tags = null) => {
    input = input.trim().toLowerCase();
    tags = tags ?? availablePartnerTags.value;
    for (const [i, availableTag] of Object.entries(tags)) {
        tags[i].show = input === '' || availableTag.value.toLowerCase().includes(input);
    }

    return tags;
}

const createNewTag = () => {

    if (loading.value.addingPartnerTag) {
        return;
    }
    loading.value.addingPartnerTag = true;

    axios.post(route('app_int.partner.partner_tags.add', {program: props.program, partner: props.partner}), {
        tag: tagSearchInput.value,
    }).then(response => {
        const status = parseInt(response.data.status || -1);
        if (status === 1) {
            partnerTags.value = response.data.partner_tags;
            availablePartnerTags.value = filterTags(tagSearchInput.value, response.data.available_partner_tags);

            isNewTagAvailable(tagSearchInput.value);
        }
    }).finally(() => {
        loading.value.addingPartnerTag = false;
    });
}

const editTags = () => {
    axios.post(route('app_int.partner.partner_tags.edit', {program: props.program, partner: props.partner}), {
        tags: partnerTags.value,
    });
}

const programPartnerTagsUpdate = (tags) => {
    availablePartnerTags.value = filterTags(tagSearchInput.value, tags.value);
}

onMounted(() => {
    availablePartnerTags.value = filterTags(tagSearchInput.value);
    isNewTagAvailable(tagSearchInput.value);
});

</script>
<style scoped></style>
